import React, { useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { RateOption, rateService } from '../../services/rate.service';
import { formatTitleCase } from '../../services/utils.service';
import styles from '../../styles/common/ServiceDetailModalV2.module.css';
import modalStyles from '../../styles/common/Modal.module.css';

interface ServiceDetailModalV2Props {
    show: boolean;
    onHide: () => void;
    rateOption: RateOption | null;
    isSimple?: boolean;
    selectedExternalIndices?: Set<number>;
    onSelectExternalService?: (newIndices: Set<number>) => void;
}

const ServiceDetailModalV2: React.FC<ServiceDetailModalV2Props> = ({
    show,
    onHide,
    rateOption,
    isSimple = false,
    selectedExternalIndices = new Set(),
    onSelectExternalService,
}) => {
    // Selection handler
    const handleToggle = (index: number) => {
        if (!rateOption || !onSelectExternalService) return;
        const nextIndices = rateService.toggleExternalService(
            rateOption,
            selectedExternalIndices,
            index
        );
        onSelectExternalService(nextIndices);
    };

    // Calcular totales
    const { baseTotal, externalTotal, grandTotal } = useMemo(() => {
        if (!rateOption) return { baseTotal: 0, externalTotal: 0, grandTotal: 0 };

        const base = rateOption.services.reduce(
            (acc, s) => acc + s.serviceSubTotal,
            0
        );

        const external = (() => {
            let total = 0;
            let globalIdx = 0;
            rateOption.externalServices?.forEach(provider => {
                provider.options.forEach(option => {
                    if (selectedExternalIndices.has(globalIdx)) {
                        total += option.serviceSubTotal;
                    }
                    globalIdx++;
                });
            });
            return total;
        })();

        return {
            baseTotal: base,
            externalTotal: external,
            grandTotal: base + external
        };
    }, [rateOption, selectedExternalIndices]);

    if (!rateOption) return null;

    const isInteractive = !!onSelectExternalService;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            size="lg"
            className={styles.serviceDetailModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Detalles de {rateOption.carrierName}
                    {rateOption.serviceIndicator && (
                        <span className={styles.serviceIndicator}> ({rateOption.serviceIndicator})</span>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className={styles.modalSubtitle}>
                    Desglose completo de servicios y costos
                </p>

                <div className={styles.serviceDetailTableContainer}>
                    <table className={styles.serviceDetailTable}>
                        <thead>
                            <tr>
                                {isInteractive && <th style={{ width: '40px' }}></th>}
                                <th>Servicio</th>
                                <th className={styles.textRight}>Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Combined List for Simple Mode */}
                            {isSimple ? (
                                <>
                                    {rateOption.services.map((service, index) => (
                                        <tr key={`service-${index}`}>
                                            {isInteractive && <td></td>}
                                            <td>
                                                <div className={styles.serviceNameCell}>
                                                    {formatTitleCase(service.serviceName)}
                                                </div>
                                            </td>
                                            <td className={styles.textRight}>
                                                {service.serviceSubTotal.toFixed(2)} {rateOption.currency}
                                            </td>
                                        </tr>
                                    ))}
                                    {rateOption.externalServices?.map((provider, pIdx) => (
                                        <React.Fragment key={`provider-${pIdx}`}>
                                            {provider.options.map((option, oIdx) => (
                                                <tr key={`ext-${pIdx}-${oIdx}`}>
                                                    {isInteractive && <td></td>}
                                                    <td>
                                                        <div className={styles.serviceNameCell}>
                                                            {formatTitleCase(option.serviceName)}
                                                        </div>
                                                    </td>
                                                    <td className={styles.textRight}>
                                                        {option.serviceSubTotal.toFixed(2)} {rateOption.currency}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </>
                            ) : (
                                <>
                                    {/* Servicios Internos */}
                                    {rateOption.services.map((service, index) => (
                                        <tr key={`service-${index}`}>
                                            {isInteractive && <td></td>}
                                            <td>
                                                <div className={styles.serviceNameCell}>
                                                    {formatTitleCase(service.serviceName)}
                                                </div>
                                            </td>
                                            <td className={styles.textRight}>
                                                {service.serviceSubTotal.toFixed(2)} {rateOption.currency}
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Servicios Externos */}
                                    {rateOption.externalServices && rateOption.externalServices.length > 0 && (
                                        <>
                                            <tr className={styles.sectionDivider}>
                                                <td colSpan={isInteractive ? 3 : 2}>
                                                    <div className={styles.dividerLine}>
                                                        <span>Servicios Adicionales</span>
                                                    </div>
                                                </td>
                                            </tr>
                                            {rateOption.externalServices.map((provider, pIdx) => {
                                                // Pre-calculate provider start index
                                                let providerStartIdx = 0;
                                                for (let i = 0; i < pIdx; i++) {
                                                    providerStartIdx += rateOption.externalServices![i].options.length;
                                                }

                                                const optionsToRender = provider.options.map((option, oIdx) => {
                                                    const currentIndex = providerStartIdx + oIdx;
                                                    const isSelected = selectedExternalIndices.has(currentIndex);

                                                    // If not interactive, only show selected ones
                                                    if (!isInteractive && !isSelected) return null;

                                                    return (
                                                        <tr key={`ext-${pIdx}-${oIdx}`} className={`${styles.externalServiceRow} ${!isSelected && isInteractive ? styles.unselected : ''}`}>
                                                            {isInteractive && (
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <label className={styles.customCheckbox}>
                                                                        <input
                                                                            type="checkbox"
                                                                            name={`modal-provider-${pIdx}`}
                                                                            checked={isSelected}
                                                                            disabled={option.mandatory && provider.options.length === 1 && provider.isRequired}
                                                                            onChange={() => handleToggle(currentIndex)}
                                                                        />
                                                                        <span className={styles.checkboxCheckmark}></span>
                                                                    </label>
                                                                </td>
                                                            )}
                                                            <td>
                                                                <div className={styles.serviceNameCell}>
                                                                    {formatTitleCase(option.serviceName)}
                                                                </div>
                                                            </td>
                                                            <td className={styles.textRight}>
                                                                {option.serviceSubTotal.toFixed(2)} {rateOption.currency}
                                                            </td>
                                                        </tr>
                                                    );
                                                });

                                                const hasVisibleOptions = optionsToRender.some(opt => opt !== null);
                                                if (!hasVisibleOptions) return null;

                                                return (
                                                    <React.Fragment key={`provider-${pIdx}`}>
                                                        <tr className={styles.providerSectionHeader}>
                                                            <td colSpan={isInteractive ? 3 : 2}>
                                                                <div className={styles.providerName}>
                                                                    {formatTitleCase(provider.providerName)} {provider.isRequired && <span className={styles.requiredSmall}>(Requerido)</span>}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {optionsToRender}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </>
                                    )}
                                </>
                            )}
                        </tbody>
                        <tfoot>
                            {isSimple ? (
                                <tr className={styles.totalRow}>
                                    <td colSpan={isInteractive ? 2 : 1}><strong>Total</strong></td>
                                    <td className={styles.textRight}>
                                        <strong>{grandTotal.toFixed(2)} {rateOption.currency}</strong>
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    <tr className={styles.subtotalRow}>
                                        <td colSpan={isInteractive ? 2 : 1}>Subtotal (servicios base)</td>
                                        <td className={styles.textRight}>
                                            {baseTotal.toFixed(2)} {rateOption.currency}
                                        </td>
                                    </tr>
                                    {externalTotal > 0 && (
                                        <tr className={styles.externalTotalRow}>
                                            <td colSpan={isInteractive ? 2 : 1}>Servicios Adicionales</td>
                                            <td className={styles.textRight}>
                                                +{externalTotal.toFixed(2)} {rateOption.currency}
                                            </td>
                                        </tr>
                                    )}
                                    <tr className={styles.totalRow}>
                                        <td colSpan={isInteractive ? 2 : 1}><strong>Total Estimado</strong></td>
                                        <td className={styles.textRight}>
                                            <strong>{grandTotal.toFixed(2)} {rateOption.currency}</strong>
                                        </td>
                                    </tr>
                                </>
                            )}
                        </tfoot>
                    </table>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={onHide}
                    className={modalStyles.closeBtn}
                >
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ServiceDetailModalV2;

